import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { salesApiService } from '../../api/salesApi';
import { usersAPI } from '../../api/api';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-GB');
};

const normalizeList = (res) => {
  if (!res?.success) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.results)) return res.results;
  return [];
};

const initialForm = {
  recipient_user: '',
  send_time: '09:00',
  is_active: true,
};

const SalesCallMailSchedulesPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'send_time', desc: false }]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const [filters, setFilters] = useState({ search: '', active: 'all' });
  const [appliedFilters, setAppliedFilters] = useState({ search: '', active: 'all' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);

  const selectedRow = useMemo(
    () => schedules.find((row) => row.id === selectedRowId) || null,
    [schedules, selectedRowId],
  );

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [scheduleRes, usersRes] = await Promise.all([
        salesApiService.getSalesCallMailSchedules(),
        usersAPI.getUsers(),
      ]);
      setSchedules(normalizeList(scheduleRes));
      if (usersRes?.success) {
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } else {
        setUsers([]);
      }
    } catch (e) {
      setError('Mail planları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRows = useMemo(() => {
    const needle = (appliedFilters.search || '').trim().toLowerCase();
    return schedules.filter((row) => {
      if (appliedFilters.active === 'active' && !row.is_active) return false;
      if (appliedFilters.active === 'inactive' && row.is_active) return false;
      if (!needle) return true;
      const hay = `${row.recipient_name || ''} ${row.recipient_email || ''} ${row.send_time || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [appliedFilters, schedules]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setIsModalOpen(true);
    setError(null);
  };

  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({
      recipient_user: selectedRow.recipient_user ? String(selectedRow.recipient_user) : '',
      send_time: (selectedRow.send_time || '').slice(0, 5),
      is_active: Boolean(selectedRow.is_active),
    });
    setIsModalOpen(true);
    setError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const recipientUserId = Number(form.recipient_user);
    if (!recipientUserId) {
      setError('Kullanıcı seçimi zorunlu.');
      return;
    }
    if (!form.send_time) {
      setError('Gönderim saati zorunlu.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const payload = {
      recipient_user: recipientUserId,
      send_time: form.send_time,
      is_active: Boolean(form.is_active),
    };
    try {
      const res = editing
        ? await salesApiService.updateSalesCallMailSchedule(editing.id, payload)
        : await salesApiService.createSalesCallMailSchedule(payload);
      if (!res?.success) {
        setError(res?.message || 'Kaydedilemedi.');
        return;
      }
      setSuccess(editing ? 'Plan güncellendi.' : 'Plan oluşturuldu.');
      setIsModalOpen(false);
      await loadData();
    } catch (err) {
      setError('Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const ok = window.confirm('Seçili plan silinsin mi?');
    if (!ok) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await salesApiService.deleteSalesCallMailSchedule(selectedRow.id);
      if (!res?.success) {
        setError(res?.message || 'Silinemedi.');
        return;
      }
      setSelectedRowId(null);
      setSuccess('Plan silindi.');
      await loadData();
    } catch (err) {
      setError('Silinemedi.');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'recipient_name',
        header: 'User',
        cell: ({ row }) => (
          <div className="space-y-0.5">
            <div className="font-medium text-gray-900 dark:text-white">{row.original.recipient_name || '-'}</div>
            <div className="text-gray-600 dark:text-gray-300">{row.original.recipient_email || '-'}</div>
          </div>
        ),
      },
      {
        accessorKey: 'send_time',
        header: 'Send Time',
        cell: ({ getValue }) => <span className="text-gray-700 dark:text-gray-300">{getValue() || '-'}</span>,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: ({ getValue }) => (
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
              getValue()
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
            }`}
          >
            {getValue() ? 'Active' : 'Passive'}
          </span>
        ),
      },
      {
        accessorKey: 'last_sent_date',
        header: 'Last Sent',
        cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span>,
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{formatDateTime(getValue())}</span>,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const SortIcon = ({ column }) => {
    const sorted = column.getIsSorted();
    if (sorted === 'asc') return <ArrowUpIcon className="w-3 h-3 text-blue-600" />;
    if (sorted === 'desc') return <ArrowDownIcon className="w-3 h-3 text-blue-600" />;
    return <ArrowsUpDownIcon className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors" />;
  };

  return (
    <div className="space-y-6">
      <div className="px-6 pt-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Sales Call Mail Schedule</h1>
      </div>

      {error ? (
        <div className="px-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">×</button>
          </div>
        </div>
      ) : null}

      {success ? (
        <div className="px-6">
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
            {success}
            <button onClick={() => setSuccess(null)} className="ml-2 text-green-600 hover:text-green-800">×</button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="px-6">
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
            Loading data...
          </div>
        </div>
      ) : null}

      <div className="px-6">
        <ActionToolbar className="flex-wrap gap-1">
          <IconActionButton icon={PlusIcon} onClick={openCreate} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={openEdit} title="Düzenle" disabled={!selectedRow} />
          <IconActionButton icon={TrashIcon} onClick={handleDelete} title="Sil" disabled={!selectedRow} />
          <IconActionButton icon={ArrowPathIcon} onClick={loadData} title="Yenile" disabled={loading} />
        </ActionToolbar>
      </div>

      <div className="px-6">
        <FilterPanel
          title="Filters"
          isOpen={isFiltersOpen}
          onToggle={() => setIsFiltersOpen((prev) => !prev)}
        >
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
              <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Search</div>
              <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">User / Email / Time</legend>
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Type to search..."
                  className="h-7 text-[11px] border-0 shadow-none p-0 bg-transparent"
                />
              </fieldset>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
              <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Status</div>
              <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Active</legend>
                <select
                  value={filters.active}
                  onChange={(e) => setFilters((prev) => ({ ...prev, active: e.target.value }))}
                  className="w-full h-7 text-[11px] bg-transparent outline-none text-gray-900 dark:text-white"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Passive</option>
                </select>
              </fieldset>
            </div>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-3">
            <FilterActions
              onClear={() => {
                const reset = { search: '', active: 'all' };
                setFilters(reset);
                setAppliedFilters(reset);
              }}
              onSearch={() => setAppliedFilters(filters)}
            />
          </div>
        </FilterPanel>
      </div>

      <div className="px-6">
        <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                          header.column.getCanSort() ? 'cursor-pointer select-none group' : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() ? <SortIcon column={header.column} /> : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={table.getAllColumns().length}
                      className="px-3 py-4 text-sm text-center text-gray-500 dark:text-gray-400"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedRowId(row.original.id)}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                        selectedRowId === row.original.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-3 py-4 whitespace-nowrap text-xs">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ResponsiveModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Mail Planını Düzenle' : 'Mail Planı Ekle'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Kullanıcı</Label>
            <select
              value={form.recipient_user}
              onChange={(e) => setForm((prev) => ({ ...prev, recipient_user: e.target.value }))}
              className="w-full h-9 px-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
            >
              <option value="">Kullanıcı seçin</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} ({u.email})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Gönderim Saati</Label>
            <Input
              type="time"
              value={form.send_time}
              onChange={(e) => setForm((prev) => ({ ...prev, send_time: e.target.value }))}
              className="h-9"
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="schedule-active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <Label htmlFor="schedule-active" className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Aktif
            </Label>
          </div>
          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
              İptal
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </form>
      </ResponsiveModal>
    </div>
  );
};

export default SalesCallMailSchedulesPage;
